import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Chip,
    Collapse,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { decompileListAggregation } from '../condition-mapper';
import type {
    DataCriticality,
    FieldCondition,
    ProductSummary,
    RequirementField,
} from '../product-requirements-config.dto';
import type { FieldOption } from './ConditionBuilder';
import CriticalityRulesEditor from './CriticalityRulesEditor';
import ListEntryRequirementEditor from './ListEntryRequirementEditor';

const CRITICALITIES: DataCriticality[] = ['mandatory', 'important', 'optional', 'irrelevant'];

/** One product's assignment as edited in the dialog (committed in a batch on Save). */
export interface AssignmentDraft {
    assigned: boolean;
    dataCriticality: DataCriticality;
    criticalityWhen: FieldCondition | null;
}

const UNASSIGNED_DRAFT: AssignmentDraft = { assigned: false, dataCriticality: 'optional', criticalityWhen: null };

interface Props {
    products: ProductSummary[];
    /** productId → draft; missing entries mean "not assigned". */
    value: Map<string, AssignmentDraft>;
    onChange: (productId: string, next: AssignmentDraft) => void;
    /** The field being edited — criticality rules are scoped to it. */
    field: Pick<RequirementField, 'code' | 'fieldType'>;
    /** Sibling codes the criticality rules may reference (group minus self). */
    fieldOptions: FieldOption[];
    disabled?: boolean;
}

/**
 * Per-product assignment editor: which products collect this (top-level) field
 * and at which criticality (GEN-642 — criticality lives on the assignment, not
 * the field). Edits stay local to the dialog's draft map; the page diffs and
 * persists them when the dialog is saved. LIST sub-fields inherit their
 * parent's assignments, so this is only rendered for top-level fields.
 */
export default function ProductAssignmentsEditor({ products, value, onChange, field, fieldOptions, disabled }: Props) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<string | null>(null);

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('Assigned products')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t(
                    'Criticality is set per product — the same field can block one service and be optional for another.',
                )}
            </Typography>
            <Stack spacing={1}>
                {products.map((product) => {
                    const draft = value.get(product.id) ?? UNASSIGNED_DRAFT;
                    const isExpanded = expanded === product.id;
                    // A LIST parent's rule slot is the entry-requirement template
                    // (per condition-dsl.md parent aggregation) as long as the rule
                    // is empty or matches it; anything else falls through to the
                    // generic rules editor (which has its own advanced fallback).
                    const useListTemplate =
                        field.fieldType === 'list' &&
                        (draft.criticalityWhen == null ||
                            decompileListAggregation(draft.criticalityWhen, field.code) != null);
                    return (
                        <Paper key={product.id} variant="outlined" sx={{ px: 1.5, py: 0.5 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Checkbox
                                    checked={draft.assigned}
                                    disabled={disabled}
                                    onChange={(e) => onChange(product.id, { ...draft, assigned: e.target.checked })}
                                    slotProps={{
                                        input: { 'aria-label': t('Assign to {{sku}}', { sku: product.sku }) },
                                    }}
                                />
                                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                                    <strong>{product.sku}</strong> — {product.name}
                                </Typography>
                                {draft.assigned && draft.criticalityWhen != null && (
                                    <Chip size="small" variant="outlined" color="info" label={t('rule')} />
                                )}
                                <FormControl size="small" sx={{ minWidth: 150 }} disabled={disabled || !draft.assigned}>
                                    <InputLabel>{t('Criticality')}</InputLabel>
                                    <Select
                                        label={t('Criticality')}
                                        value={draft.dataCriticality}
                                        onChange={(e) =>
                                            onChange(product.id, {
                                                ...draft,
                                                dataCriticality: e.target.value as DataCriticality,
                                            })
                                        }
                                    >
                                        {CRITICALITIES.map((c) => (
                                            <MenuItem key={c} value={c}>
                                                {t(c)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton
                                    size="small"
                                    disabled={!draft.assigned}
                                    onClick={() => setExpanded(isExpanded ? null : product.id)}
                                    aria-label={t('Criticality rules')}
                                >
                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                            </Stack>
                            <Collapse in={draft.assigned && isExpanded} unmountOnExit>
                                <Box sx={{ pb: 1.5, pl: 1 }}>
                                    {useListTemplate ? (
                                        <ListEntryRequirementEditor
                                            value={draft.criticalityWhen}
                                            ownCode={field.code}
                                            disabled={disabled}
                                            onChange={(criticalityWhen) =>
                                                onChange(product.id, { ...draft, criticalityWhen })
                                            }
                                        />
                                    ) : (
                                        <CriticalityRulesEditor
                                            key={`${product.id}-${draft.assigned}`}
                                            value={draft.criticalityWhen}
                                            staticCriticality={draft.dataCriticality}
                                            fieldOptions={fieldOptions}
                                            onChange={(criticalityWhen) =>
                                                onChange(product.id, { ...draft, criticalityWhen })
                                            }
                                        />
                                    )}
                                </Box>
                            </Collapse>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
}
